import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

function ChatStatsClassPicker({ classes, value, onChange }) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')
    const containerRef = useRef(null)
    const inputRef = useRef(null)

    const selected = classes.find((item) => item.id === value) || null

    const filtered = useMemo(() => {
        const query = search.trim().toLowerCase()
        if (!query) return classes
        return classes.filter(
            (item) =>
                item.name.toLowerCase().includes(query) ||
                String(item.year || '')
                    .toLowerCase()
                    .includes(query)
        )
    }, [classes, search])

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setOpen(false)
                setSearch('')
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const pick = (classId) => {
        onChange(classId)
        setOpen(false)
        setSearch('')
    }

    const label = (item) => (item.year ? t('chatStatsClassOption', { year: item.year, name: item.name }) : item.name)

    return (
        <div style={{ position: 'relative' }} ref={containerRef}>
            <button
                type="button"
                onClick={() => {
                    setOpen((prev) => !prev)
                    if (!open) setTimeout(() => inputRef.current?.focus(), 0)
                }}
                style={{
                    padding: '9px 14px',
                    borderRadius: 999,
                    border: '1px solid #e4e3f0',
                    background: '#fff',
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#5e639b',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    whiteSpace: 'nowrap',
                    minWidth: 160,
                    justifyContent: 'space-between',
                }}
            >
                {selected ? label(selected) : t('chatStatsAllClasses')}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>

            {open && (
                <div
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        left: 0,
                        zIndex: 10,
                        width: 260,
                        background: '#fff',
                        border: '1px solid #e4e3f0',
                        borderRadius: 12,
                        boxShadow: '0 12px 30px rgba(45, 43, 69, 0.14)',
                        overflow: 'hidden',
                    }}
                >
                    <div style={{ padding: 8, borderBottom: '1px solid #e4e3f0' }}>
                        <input
                            ref={inputRef}
                            type="text"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder={t('chatStatsSearchClasses')}
                            style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid #e4e3f0', fontSize: 13, outline: 'none' }}
                        />
                    </div>

                    <div style={{ maxHeight: 220, overflowY: 'auto', overscrollBehavior: 'contain' }}>
                        <div
                            onClick={() => pick('')}
                            style={{
                                padding: '9px 14px',
                                fontSize: 13,
                                fontWeight: value ? 500 : 700,
                                color: '#5e639b',
                                cursor: 'pointer',
                                background: value ? 'transparent' : '#f5f3fa',
                            }}
                        >
                            {t('chatStatsAllClasses')}
                        </div>
                        {filtered.length === 0 ? (
                            <div style={{ padding: '10px 14px', fontSize: 13, color: '#8e8fb4' }}>{t('chatStatsNoMatchingClasses')}</div>
                        ) : (
                            filtered.map((item) => (
                                <div
                                    key={item.id}
                                    onClick={() => pick(item.id)}
                                    style={{
                                        padding: '9px 14px',
                                        fontSize: 13,
                                        fontWeight: item.id === value ? 700 : 500,
                                        color: '#2d2b45',
                                        cursor: 'pointer',
                                        background: item.id === value ? '#f5f3fa' : 'transparent',
                                    }}
                                >
                                    {label(item)}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default ChatStatsClassPicker
